import type { PluginMcpServer } from '@crowdex/plugin-contracts';

/** A single selectable model, flattened from a select config option. */
export type ModelChoice = { configId: string; value: string; name: string };

type SkillLike = {
  name: string;
  displayName?: string;
  description?: string;
  shortDescription?: string;
};

/** Skills whose name/displayName/description/shortDescription matches the query. */
export function filterSkills<T extends SkillLike>(
  skills: readonly T[],
  query: string | null,
): readonly T[] {
  if (query === null) return [];
  return skills.filter((skill) =>
    [skill.name, skill.displayName, skill.description, skill.shortDescription]
      .filter((value): value is string => value !== undefined)
      .some((value) => value.toLowerCase().includes(query)),
  );
}

/** A single MCP server row for the composer's MCP submenu. */
export type McpServerMenuItem = { name: string; detail: string };

/** Short, human-readable transport hint for an MCP server row: the host for a
 *  remote (http/sse) server, or the launch command for a local one. */
function mcpServerDetail(server: PluginMcpServer): string {
  if ('type' in server) {
    try {
      return new URL(server.url).host;
    } catch {
      return server.type.toUpperCase();
    }
  }
  return server.command;
}

/**
 * Build the MCP submenu rows from the enabled plugins' MCP servers. Dedupes by
 * server name (multiple plugins can technically declare the same name), sorts
 * alphabetically for a stable order, and attaches a transport detail. Pure.
 */
export function buildMcpServerMenuItems(
  servers: readonly PluginMcpServer[],
): readonly McpServerMenuItem[] {
  const byName = new Map<string, McpServerMenuItem>();
  for (const server of servers) {
    if (byName.has(server.name)) continue;
    byName.set(server.name, { name: server.name, detail: mcpServerDetail(server) });
  }
  return [...byName.values()].sort((a, b) => a.name.localeCompare(b.name));
}
